import { useEffect, useRef, useState } from 'react';
import { groupTripApi } from '../../api/groupTripApi';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { groupTripErrorMessage, normalizeDisruptionBoard } from './groupTripModel';
import { useMemberSafetyDialogA11y } from './memberSafetyDialogA11y';

export default function GroupTripD1Participation({ groupTripId }) {
  const { t } = useTravelTranslation();
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmTravelerId, setConfirmTravelerId] = useState('');

  useEffect(() => {
    if (!groupTripId || typeof groupTripApi.readDisruption !== 'function') return undefined;
    let cancelled = false;
    groupTripApi.readDisruption(groupTripId)
      .then((payload) => {
        if (!cancelled) setRows(normalizeDisruptionBoard(payload).items);
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      });
    return () => { cancelled = true; };
  }, [groupTripId]);

  if (typeof groupTripApi.readDisruption !== 'function') return null;

  async function reload() {
    const payload = await groupTripApi.readDisruption(groupTripId);
    setRows(normalizeDisruptionBoard(payload).items);
  }

  async function run(action) {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await action();
      await reload();
      setConfirmTravelerId('');
    } catch (cause) {
      setError(groupTripErrorMessage(cause, t, 'travel.common.actionFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="group-trips__panel" data-testid="d1-participation">
      <h2>{t('travel.groupTrip.d1Heading')}</h2>
      {error ? <p role="alert">{error}</p> : null}
      <ul className="group-trips__list">
        {rows.map((row) => {
          // 目前獨立行動以 backend 的 effectivePeriodId 為準，不用本機時間重算。
          const status = row.participationEnded
            ? t('travel.groupTrip.d1ParticipationEnded')
            : row.effectivePeriodId
              ? t('travel.groupTrip.d1IndependentActive')
              : '';
          return (
            <li className="group-trips__card" key={row.travelerId} data-testid={`d1-${row.travelerId}`}>
              <p>
                {row.displayName}
                {status ? ` · ${status}` : ''}
              </p>
              <div className="group-trips__actions">
                {row.canStartPeriod ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => run(() => groupTripApi.startIndependentPeriod(groupTripId, row.travelerId, {}))}
                  >
                    {t('travel.groupTrip.d1Start')}
                  </button>
                ) : null}
                {row.canEndPeriod && row.effectivePeriodId ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => run(() => groupTripApi.endIndependentPeriod(
                      groupTripId, row.travelerId, row.effectivePeriodId,
                    ))}
                  >
                    {t('travel.groupTrip.d1End')}
                  </button>
                ) : null}
                {row.canFormalEnd ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setConfirmTravelerId(row.travelerId)}
                  >
                    {t('travel.groupTrip.d1EndParticipation')}
                  </button>
                ) : null}
              </div>
              {confirmTravelerId === row.travelerId ? (
                <ParticipationEndConfirm
                  busy={busy}
                  warning={t('travel.groupTrip.d1EndWarning')}
                  confirmLabel={t('travel.groupTrip.d1EndParticipation')}
                  cancelLabel={t('travel.groupTrip.d1Cancel')}
                  testId={`d1-confirm-${row.travelerId}`}
                  onConfirm={() => run(() => groupTripApi.endGroupParticipation(groupTripId, row.travelerId, {}))}
                  onCancel={() => setConfirmTravelerId('')}
                />
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function ParticipationEndConfirm({
  busy, warning, confirmLabel, cancelLabel, testId, onConfirm, onCancel,
}) {
  const panelRef = useRef(null);
  useMemberSafetyDialogA11y({ open: true, onClose: onCancel, busy, panelRef });
  return (
    <div
      className="member-safety-dialog"
      role="dialog"
      aria-modal="true"
      data-testid={testId}
      onClick={() => { if (!busy) onCancel(); }}
    >
      <div
        className="member-safety-dialog__panel"
        ref={panelRef}
        onClick={(event) => event.stopPropagation()}
      >
        <p>{warning}</p>
        <div className="group-trips__actions">
          <button type="button" disabled={busy} onClick={onConfirm}>
            {confirmLabel}
          </button>
          <button type="button" disabled={busy} onClick={onCancel}>
            {cancelLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
